import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';

interface MobileMenuProps {
    tabs: Array<[string, string | null, Array<[string, string]>?]>;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ tabs }) => {
    // Set isOpen to true by default so menu appears on page load
    const [isOpen, setIsOpen] = useState(true);
    const [openSubmenu, setOpenSubmenu] = useState<number | null>(null);
    
    // Get current location for navigation detection
    const location = useLocation();
    
    // Effect to ensure menu opens on page navigation
    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);
    
    const navigate = useNavigate();

    const handleClearUrlAndNavigate = () => {
        navigate("", { replace: true });
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleMenuItemClick = (tabLink: string) => {
        setIsOpen(false);
        
        // For hash links, scroll to element on current page
        if (tabLink.startsWith('#')) {
            const id = tabLink.substring(1);
            const element = document.getElementById(id);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } else {
            handleClearUrlAndNavigate();
            if (tabLink.startsWith('/')) {
                scrollToTop();
            }
        }
    };

    const toggleSubmenu = (index: number) => {
        setOpenSubmenu(openSubmenu === index ? null : index);
    };

    const isTabActive = (tabLink: string | null): boolean => {
        if (!tabLink || tabLink === '' || tabLink.startsWith('#')) return false;
        const normalized = tabLink.startsWith('/') ? tabLink : `/${tabLink}`;
        return location.pathname === normalized;
    };

    const isSubmenuActive = (subItems: Array<[string, string]>): boolean =>
        subItems.some(([, link]) => location.pathname === link);

    return (
        <div className="mobile-menu-container" style={{
                display: "flex",
                justifyContent: "space-between", // Changed from center to space-between
                alignItems: "center",
                width: "100%",
                height: "100%",
                padding: "0 15px"
            }}>
            <a href="/" style={{display: "flex", textDecoration: 'none', alignItems: 'center'}}>
                <img style={{ height: "40px", width: "40px" }} src= "/images/logos/surfLogoWhite.png" alt="" />
                <h1 className="mobile-title" style={{ padding:'10px', color: "var(--color-primary)", fontWeight: "700", fontSize: "clamp(20px, 5vw, 39px)", fontFamily: "'NCAA Missouri Tigers', Arial", whiteSpace: "nowrap" }}>Mizzou Surf</h1>
            </a>
            <button 
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Toggle menu"
                style={{
                    display: "flex",
                    alignItems: "center",
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '10px',
                    zIndex: 10,
                    marginLeft: 'auto', // Push to the right
                    width: 'auto'
                }}
            >
                <div className={`hamburger-icon ${isOpen ? 'open' : ''}`}>
                    <span style={{ backgroundColor: 'var(--color-primary)' }}></span>
                    <span style={{ backgroundColor: 'var(--color-primary)' }}></span>
                    <span style={{ backgroundColor: 'var(--color-primary)' }}></span>
                </div>
            </button>
            
            {isOpen && (
                <div className="mobile-menu" style={{
                        position: "fixed", 
                        top: "72px",
                        left: 0,
                        right: 0,
                        width: "100%", 
                        backgroundColor: "var(--color-nav-bg-solid)",
                        zIndex: 100,
                        padding: "20px",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                        overflowY: "auto",
                        maxHeight: "calc(100vh - 72px)" // Updated to match header height
                    }}>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                        {tabs.map((tab, index) => {
                            const active = tab[2] ? isSubmenuActive(tab[2]) : isTabActive(tab[1]);
                            return (
                            <li key={index} className="mobile-menu-item" style={{
                                padding: "15px 0",
                                paddingLeft: active ? "12px" : "0",
                                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                                borderLeft: active ? "3px solid var(--color-primary)" : "3px solid transparent",
                                fontFamily: "'Play', Arial",
                                transition: "border-color 0.2s ease",
                            }}>
                                {tab[2] && tab[2].length > 0 ? (
                                    <>
                                        <div 
                                            className="mobile-menu-item-with-submenu"
                                            onClick={() => toggleSubmenu(index)}
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                color: "white",
                                                fontSize: "18px",
                                                cursor: "pointer",
                                                fontFamily: "'Play', Arial"
                                            }}
                                        >
                                            <span style={{ color: "var(--color-primary)" }}>{tab[0]}</span>
                                            <span className={`submenu-arrow ${openSubmenu === index ? 'open' : ''}`} style={{ fontSize: "12px" }}>▼</span>
                                        </div>
                                        {openSubmenu === index && (
                                            <ul className="mobile-submenu" style={{
                                                marginTop: "10px",
                                                paddingLeft: "15px",
                                                listStyle: "none"
                                            }}>
                                                {tab[2].map((subItem, subIndex) => (
                                                    <li key={subIndex} style={{
                                                        padding: "10px 0"
                                                    }}>
                                                        {subItem[1].startsWith('/') ? (
                                                            <Link 
                                                                to={subItem[1]} 
                                                                onClick={() => handleMenuItemClick(subItem[1])}
                                                                style={{
                                                                    color: "white",
                                                                    textDecoration: "none",
                                                                    fontSize: "16px",
                                                                    display: "block",
                                                                    fontFamily: "'Play', Arial"
                                                                }}
                                                            >
                                                                {subItem[0]}
                                                            </Link>
                                                        ) : (
                                                            <a 
                                                                href={subItem[1]} 
                                                                onClick={() => handleMenuItemClick(subItem[1])}
                                                                style={{
                                                                    color: "white",
                                                                    textDecoration: "none",
                                                                    fontSize: "16px",
                                                                    display: "block",
                                                                    fontFamily: "'Play', Arial"
                                                                }}
                                                            >
                                                                {subItem[0]}
                                                            </a>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {tab[1] && tab[1].startsWith('/') ? (
                                            <Link 
                                                to={tab[1]} 
                                                onClick={() => handleMenuItemClick(tab[1] as string)}
                                                style={{
                                                    color: "var(--color-primary)",
                                                    textDecoration: "none",
                                                    fontSize: "18px",
                                                    display: "block",
                                                    fontFamily: "'Play', Arial"
                                                }}
                                            >
                                                {tab[0]}
                                            </Link>
                                        ) : (
                                            <a 
                                                href={tab[1] || '#'} 
                                                onClick={() => handleMenuItemClick(tab[1] as string || '#')}
                                                style={{
                                                    color: "var(--color-primary)",
                                                    textDecoration: "none",
                                                    fontSize: "18px",
                                                    display: "block",
                                                    fontFamily: "'Play', Arial"
                                                }}
                                            >
                                                {tab[0]}
                                            </a>
                                        )}
                                    </>
                                )}
                            </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default MobileMenu;